import dayjs from 'dayjs';
import { currentYear, formatHandler, returnDate, todayDate } from 'services';

/**
 * Parse a date string into a Date object.
 *
 * @param {string} dateString - The date string to parse.
 * @param {string} [separator='/'] - The separator used in the date string.
 * @returns {Date | undefined} The parsed Date object, or undefined if the input is invalid.
 *
 * @example
 * // Example usage:
 * const parsedDate = getParsedDate('12/31/2022');
 * // Returns: Date object representing December 31, 2022
 */
export const getParsedDate = (dateString: string, separator?: string) => {
  if (dateString) {
    const parts = dateString.split(separator || '/');
    const [month, day, year] = parts.map(Number);

    const parsedDate = dayjs(`${year}-${month}-${day}`, {
      format: 'YYYY-MM-DD',
      locale: 'en'
    });

    if (!day || !month || !year) {
      return new Date();
    }

    if (parsedDate.isValid()) {
      return parsedDate.toDate();
    }
  }

  return undefined;
};

/**
 * Get a custom formatted date string.
 *
 * @param {string | null} dateString The input date string.
 * @param {string} currentYearFormat The format for the current year.
 * @param {string} otherYearFormat The format for other years.
 * @returns {string | null} The custom formatted date string.
 *
 * @example
 * const dateString = '2022-12-31';
 * const formattedDate = getCustomFormattedDate(dateString, 'MMM D', 'MM/DD/YY');
 * // Returns: 'Dec 31'
 */
export const getCustomFormattedDate = (
  dateString: string | null,
  currentYearFormat: 'MMMM D, YYYY' | 'MMM D' | 'MM/DD/YYYY',
  otherYearFormat: 'MMMM D, YYYY' | 'MM/DD/YY'
): string | null => {
  if (!dateString) {
    return dateString;
  }

  const currentDate = dayjs();
  const parsedDate = dayjs(dateString);
  const isCurrentYear = parsedDate.isSame(currentDate, 'year');

  return isCurrentYear
    ? parsedDate.format(currentYearFormat)
    : parsedDate.format(otherYearFormat);
};

/**
 * Check if a date string is valid.
 *
 * @param {string} dateString The date string to validate.
 * @returns {boolean} True if the date is valid, false otherwise.
 *
 * @example
 * const isValid = isDateValid('12/31/2022');
 * // Returns: true
 */
export const isDateValid = (dateString?: string): boolean => {
  const regex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;

  if (!dateString || !regex.test(dateString)) {
    return false;
  }

  const [month, day, year] = dateString.split('/').map(Number);

  const isValidDate = (y: number, m: number, d: number): boolean => {
    const currentDate = new Date();
    const inputDate = new Date(y, m - 1, d);

    return (
      inputDate.getFullYear() === y &&
      inputDate.getMonth() === m - 1 &&
      inputDate.getDate() === d &&
      inputDate <= currentDate
    );
  };

  return isValidDate(year, month, day);
};

/**
 * Get the maximum valid date based on the input date string.
 *
 * @param {string} inputDate The input date string.
 * @returns {string} The maximum valid date string.
 *
 * @example
 * const maxDate = getMaxValidDate('12/31/2022');
 * // Returns: '12/31/2022'
 */
export const getMaxValidDate = (inputDate: string): string => {
  const todayDateObject = new Date();
  const [inputMonth, inputDay, inputYear] = inputDate.split('/');

  let month: string = inputMonth || '__';
  let day: string = inputDay || '__';
  let year: string = inputYear || '____';

  if (!String(month).includes('_') && (+month < 1 || +month > 12)) {
    month = '12';
  }

  if (!String(day).includes('_') && (+day < 1 || +day > 31)) {
    day = '31';
  }

  if (!String(year).includes('_') && +year > todayDateObject.getFullYear()) {
    year = String(todayDateObject.getFullYear());
  }

  return `${month}/${day}/${year}`;
};

/**
 * Format a date string into a human-readable format.
 *
 * @param {string} dateString - The date string to format.
 * @param {string} format - format type "MM-DD-YYYY" etc.
 * @param outputCurrentYearFormat
 * @param outputAnotherYearFormat
 * @returns {string} A human-readable format of the date.
 *
 * @example
 * // Example usage:
 * const formattedDate = getHumanReadableDate('12-12-2024', 'MM-DD-YYYY');
 * // Output: "Friday, Dec 12, 2024"
 */
export const getHumanReadableDate = (
  dateString: string,
  format: string,
  outputCurrentYearFormat?: string,
  outputAnotherYearFormat?: string
): string => {
  const parsedDate = dayjs(dateString, format);
  const isCurrentYear = parsedDate.isSame(todayDate, 'year');
  const formattedDate = isCurrentYear
    ? parsedDate.format(outputCurrentYearFormat || 'ddd, MMM D')
    : parsedDate.format(outputAnotherYearFormat || 'ddd, MMM D, YYYY');

  if (parsedDate.isSame(todayDate, 'day')) {
    return 'Today';
  }
  if (parsedDate.isSame(todayDate.add(1, 'day'), 'day')) {
    return 'Tomorrow';
  }
  if (parsedDate.isSame(todayDate.subtract(1, 'day'), 'day')) {
    return 'Yesterday';
  }
  return formattedDate;
};

/**
 * Formats a date string based on whether it's the current year or not.
 * If the year is the current year, it formats the date as "MMM D" (e.g., "Jul 26").
 * If it's not the current year, it formats it as "MM/DD/YY" (e.g., "12/28/22").
 * @param {string} dateString - The input date string in the format 'YYYY-MM-DD'.
 * @returns {string} The formatted date string.
 */
export const formatCustomDate = (dateString: string): string => {
  const date = returnDate(dateString);
  const isCurrentYear = date.year() === currentYear;
  const outputFormat = isCurrentYear ? 'MMM D' : 'MM/DD/YY';
  return date.format(outputFormat);
};

/**
 * Formats a date string to "Thu, Mar 28" format.
 * @param {string} dateString - The input date string in the format 'YYYY-MM-DD'.
 * @param {string} outputFormat - The output format
 * @returns {string} The formatted date string.
 */
export const getCustomFormattedDay = (
  dateString: string,
  outputFormat: string = 'ddd, MMM D'
): string => {
  const date = returnDate(dateString, 'YYYY-MM-DD').toDate();
  const formattedDate = formatHandler(date, '', outputFormat);
  const year = returnDate(dateString, 'YYYY-MM-DD').year();
  if (year !== currentYear) {
    return `${formattedDate}, ${year}`;
  }
  return formattedDate;
};

export const formatDate = (date: string, format: string) =>
  dayjs(date).format(format);
